import { Button } from '@shared/components/ui/button';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfitabilitySummaryShippingCosts } from '../../hooks';
import type {
  ProfitabilitySummaryBreakdownQuery,
  ProfitabilitySummaryShippingCostsTotals,
} from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { formatSummaryShippingServiceLabel } from '../../utils/summaryShippingCostsDisplay';

const PER_PAGE = 20;

interface ProfitabilitySummaryShippingCostsTableProps {
  filters: ProfitabilitySummaryBreakdownQuery | undefined;
  locale: string;
  embedded?: boolean;
  enabled?: boolean;
}

function SourceAmount({ value, locale, currency }: { value: number | undefined; locale: string; currency: string }) {
  if (value == null || value === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <>{formatProfitabilityAmount(value, locale, currency)}</>;
}

function ShippingTotalsRow({
  label,
  totals,
  locale,
  currency,
}: {
  label: string;
  totals: ProfitabilitySummaryShippingCostsTotals;
  locale: string;
  currency: string;
}) {
  return (
    <TableRow className="border-border bg-muted/40 font-medium hover:bg-muted/40">
      <TableCell className="text-sm text-foreground">{label}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">{totals.orders_count.toLocaleString(locale)}</TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityAmount(totals.average_cost, locale, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        {formatProfitabilityAmount(totals.total, locale, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SourceAmount value={totals.by_source?.accounting} locale={locale} currency={currency} />
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SourceAmount value={totals.by_source?.default} locale={locale} currency={currency} />
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SourceAmount value={totals.by_source?.estimated} locale={locale} currency={currency} />
      </TableCell>
    </TableRow>
  );
}

export const ProfitabilitySummaryShippingCostsTable = ({
  filters,
  locale,
  embedded = false,
  enabled = true,
}: ProfitabilitySummaryShippingCostsTableProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const [page, setPage] = useState(1);
  const noServiceLabel = t('summary.shipping_costs_breakdown.no_service');

  useEffect(() => {
    setPage(1);
  }, [
    filters?.start_date,
    filters?.end_date,
    filters?.shop_id,
    filters?.payment_method_id,
    filters?.shipment_status_id,
  ]);

  const query = useMemo(() => (filters ? { ...filters, page, per_page: PER_PAGE } : undefined), [filters, page]);

  const { data, isLoading, isError, isFetching } = useProfitabilitySummaryShippingCosts(query, !!filters && enabled);
  const rows = data?.data ?? [];
  const meta = data?.meta;
  const colCount = 7;
  const showPagination = meta != null && (meta.total > 0 || meta.last_page > 1);

  return (
    <div data-testid="profitability-summary-shipping-costs-table">
      {!embedded && (
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-lg font-semibold text-title">{t('summary.shipping_costs_breakdown.title')}</h2>
          <InfoTooltip
            text={t('summary.shipping_costs_breakdown.hint')}
            dataTestId="profitability-summary-shipping-costs-info"
            iconClassName="w-4 h-4"
          />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_service')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_orders')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_avg_cost')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_total')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_accounting')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_default')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('summary.shipping_costs_breakdown.col_estimated')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">
                  …
                </TableCell>
              </TableRow>
            )}

            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('empty.error.title')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('summary.shipping_costs_breakdown.empty')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map((row, index) => (
                <TableRow
                  key={`${row.courier?.id ?? 'none'}-${row.courier_service?.id ?? index}`}
                  className="border-border"
                  data-testid={`profitability-summary-shipping-costs-row-${row.courier_service?.id ?? index}`}
                >
                  <TableCell className="max-w-[320px] truncate text-sm text-foreground">
                    {formatSummaryShippingServiceLabel(row, noServiceLabel)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {row.orders_count.toLocaleString(locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatProfitabilityAmount(row.average_cost, locale, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-medium">
                    {formatProfitabilityAmount(row.total, locale, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    <SourceAmount value={row.by_source?.accounting} locale={locale} currency={currency} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    <SourceAmount value={row.by_source?.default} locale={locale} currency={currency} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    <SourceAmount value={row.by_source?.estimated} locale={locale} currency={currency} />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && !isError && data?.totals && rows.length > 0 && (
              <ShippingTotalsRow
                label={t('summary.shipping_costs_breakdown.total_row')}
                totals={data.totals}
                locale={locale}
                currency={currency}
              />
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && meta && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t('pagination.total', { total: meta.total })}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('pagination.page', { current: meta.current_page, last: meta.last_page })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={meta.current_page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="profitability-summary-shipping-costs-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={meta.current_page >= meta.last_page || isFetching}
              onClick={() => setPage((p) => p + 1)}
              data-testid="profitability-summary-shipping-costs-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
